import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  QueryList,
  AfterViewInit
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ChannelUIds,
  CommunicationTemplate,
  CommunicationTemplateViewControl,
  CommunicationTemplateViewControlOverride,
  ControlActionType,
  MassNotificationTemplate,
  MessageSettings,
  SkippedGroupChannels,
  TemplateViewControlAction,
  TemplateViewControlType
} from 'src/app/modules/communications/store/communications';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';
import { catchError, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { FieldToValueMapping, SelectedElementEvent } from '../design-elements/design-elements.models';
import { TemplateDesignElementDirective } from '../design-elements/design-elements.directive';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { cloneDeep } from 'lodash-es';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromComm from 'src/app/modules/communications/store/communications';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { TemplateDesignElementsRegistration } from '../design-elements/design-elements.map';

@Component({
    selector: 'topo-comms-template-designer',
    templateUrl: './designer.component.html',
    styleUrls: ['./designer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TemplateDesigner implements OnInit, OnDestroy, AfterViewInit {
  private mutationObservers: MutationObserver[] = [];

  @ViewChildren('editableElement') editableElements!: QueryList<ElementRef>;

  @Input() messageSettings: MessageSettings;

  _template: CommunicationTemplate;
  @Input() set template(value: CommunicationTemplate) {
    if (!!!value || !!!this._template || value?.uId !== this._template.uId) {
      this.selectedControlUId = null;
    }
    this._template = cloneDeep(value);
    this._template.viewControls = this._template.viewControls.filter(x => x.controlType != TemplateViewControlType.Attachments);
    this.needToApplyOverrides = true;
    this.headerDatasetName = '';
    this.headerDocumentName = '';
    this.headerDatasetType = '';

    this.showSlackHeader = value?.channelUId === ChannelUIds.SlackChannelUId;
    this.showTeamsHeader = value?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId;
    this.isWhatsApp = value?.channelUId === ChannelUIds.WhatsAppChannelUId;
    this.isSms = value?.channelUId === ChannelUIds.TwilioChannelUId || value?.channelUId === ChannelUIds.HorizonSmsChannelUId;
    this.isEmail = value?.channelUId === ChannelUIds.TopoEmailChannelUId || value?.channelUId === ChannelUIds.SendGridEmailChannelUId;
    this.isSlack = value?.channelUId === ChannelUIds.SlackChannelUId;
    this.isTeams = value?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId;
    this.isTts = value?.channelUId === ChannelUIds.TwilioTtsChannelUId;

    this.hiddenControls = [];
    this.busyControls = [];

    if (this.isWhatsApp) {
      this.store.dispatch(fromComm.getWhatsAppTemplateGroupFromApi({ templateGroupUId: this.defaultWhatsAppTemplate }));
    }
    // set the width of the control based on the type of template
    if (this.isSms || this.isWhatsApp || this.isTts) {
      this._width = this.mobileMessageWidth;
    } else if (this.isSlack || this.isTeams) {
      this._width = this.slackMessageWidth;
    } else {
      this._width = this.defaultWidth;
    }

    this.monitorControlOverrides();
  }

  get template(): CommunicationTemplate {
    return this._template;
  }

  @Input() editable: boolean = true;
  @Input() culture: string;
  @Input() previewMessageVisible: boolean = false;

  @Input() editableControlTypes?: TemplateViewControlType[];
  @Input() deletableControlTypes?: TemplateViewControlType[];

  defaultWidth: string = '800px';

  _width: string = this.defaultWidth;
  @Input() set width(value: string) {
    this._width = value;
    this.defaultWidth = value;
  }

  get width(): string {
    return this._width;
  }

  @Input() mobileMessageWidth: string = '392px';
  @Input() slackMessageWidth: string = '500px';

  _overrides: CommunicationTemplateViewControlOverride[];

  @Input() set overrides(value: CommunicationTemplateViewControlOverride[]) {
    this._overrides = value;
    this.needToApplyOverrides = true;
    this.monitorControlOverrides();
  }

  whatsAppText: string;

  @Input() set documentContext(value: { dataset: Dataset; document: Document }) {
    this.headerDatasetName = value?.dataset?.name;
    this.headerDocumentName = '';
    this.headerDatasetType = '';

    if (!!!value) {
      return;
    }

    if (!!value?.document) {
      const doc = value.document as any;
      this.headerDocumentName = doc.summary ?? doc.name ?? doc.first_name + ' ' + doc.last_name;
    }

    let datasetType = '';

    switch (value?.dataset?.type) {
      case DatasetType.Alerts:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-1-SINGULAR';
        break;
      case DatasetType.Assets:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-2-SINGULAR';
        break;
      case DatasetType.PointOfInterest:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-3';
        break;
      case DatasetType.Camera:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-5';
        break;
      case DatasetType.Alarm:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-6';
        break;
      case DatasetType.Incident:
        datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-9';
        break;
    }

    if (!!datasetType) {
      this.headerDatasetType = this.translateService.instant(datasetType);
    }
  }

  @Output() changed = new EventEmitter<CommunicationTemplate>();
  @Output() controlChanged = new EventEmitter<{
    template: CommunicationTemplate;
    control: CommunicationTemplateViewControl;
    settings: any;
  }>();

  @Output() controlSelected = new EventEmitter<SelectedElementEvent>();
  @Output() controlDeleted = new EventEmitter<SelectedElementEvent>();
  @Output() controlEdited = new EventEmitter<SelectedElementEvent>();

  staticTextTranslations: any;
  orgLanguages: string[];
  selectedControlUId: string;
  needToApplyOverrides: boolean;
  showSlackHeader: boolean;
  showTeamsHeader: boolean;
  isWhatsApp: boolean;
  isSms: boolean;
  isEmail: boolean;
  isSlack: boolean;
  isTeams: boolean;
  isTts: boolean;
  headerDatasetName: string;
  headerDatasetType: string;
  headerDocumentName: string;
  destroy$ = new Subject<void>();
  defaultWhatsAppTemplate = 'd98f33b6-edd0-46f4-9442-a791f3225009';
  hiddenControls: string[] = [];
  busyControls: string[] = [];

  @ViewChildren(TemplateDesignElementDirective) designControls;

  constructor(
    private templateDesignerService: TemplateDesignerService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private store: Store<any>
  ) {}

  ngOnInit(): void {
    if (!!!this.templateDesignerService.staticTextTranslations) {
      this.templateDesignerService.init(this.editable);
    }
    this.orgLanguages = this.templateDesignerService.userProfile?.culture?.orgCultures?.split(',');

    this.store
      .select(fromComm.selectWhatsAppTemplates)
      .pipe(
        withLatestFrom(this.store.select(fromUserProfile.selectOrganization)),
        takeUntil(this.destroy$),
        filter(t => !!t)
      )
      .subscribe(([templates, org]) => {
        this.whatsAppText = templates?.find(t => t.locale === this.culture)?.textContent;
        if (!this.editable) {
          this.whatsAppText = this.whatsAppText?.replace('{{org_name}}', org.displayName);
        }
      });

    this.translateService.onLangChange.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.calculateTotalCharacters();
    });
  }

  ngAfterViewInit() {
    this.setupMutationObservers();
    this.calculateTotalCharacters();

    this.editableElements.changes.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.setupMutationObservers();
      this.calculateTotalCharacters();
    });
  }

  private setupMutationObservers(): void {
    // Disconnect existing observers
    this.mutationObservers.forEach(observer => observer.disconnect());
    this.mutationObservers = [];

    // Setup new observers for each element
    this.editableElements.forEach(elementRef => {
      const observer = new MutationObserver(() => {
        this.calculateTotalCharacters();
      });
      observer.observe(elementRef.nativeElement, {
        characterData: true,
        subtree: true,
        childList: true
      });
      this.mutationObservers.push(observer);
    });
  }

  // Updated calculateTotalCharacters method
  public calculateTotalCharacters(): void {
   if (!this.isSms) return; // Only calculate when on SMS tab

    let totalTextLength = 0;

    this.editableElements.forEach((elementRef) => {
      // Check if element is hidden
      if (elementRef.nativeElement.classList.contains('hidden')) return;

      // Get raw text content including all whitespace
      const text = elementRef.nativeElement.textContent || '';

      // Count all characters including spaces and line breaks
      totalTextLength += text.trim().length;
    });
    // Pass both count and SMS status to service
    this.templateDesignerService.updateCount(totalTextLength, this.isSms);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.controlSelected.complete();
    this.controlDeleted.complete();
    this.controlEdited.complete();

    // Cleanup MutationObservers
    this.mutationObservers.forEach(observer => observer.disconnect());
    this.mutationObservers = [];
  }

  private monitorControlOverrides(): void {
    if (this.needToApplyOverrides) {
      let changed = false;
      if (!!this._template && (this.designControls?._results ?? []).length > 0) {
        this._overrides
          ?.filter(o => o.templateUId === this._template?.uId)
          .forEach(o => {
            if (o.deleted === true) {
              const controlIndex = this._template?.viewControls?.findIndex(c => c.uId === o.controlUId);
              if (controlIndex >= 0) {
                this._template?.viewControls.splice(controlIndex, 1);
                changed = true;
              }
            } else {
              const controls = this._template?.viewControls?.filter(c => c.uId === o.controlUId);
              controls?.forEach(c => {
                const done = this.setRuntimeData(c.uId, o.settings);
                if (done) {
                  changed = true;
                }
              });
            }
          });
      }
      if (changed) {
        this.changed.emit(this._template);
        this.needToApplyOverrides = false;
      } else {
        setTimeout(() => {
          this.monitorControlOverrides();
        }, 500);
      }
    }
  }

  clearSelected(): void {
    this.selectedControlUId = null;
  }

  onDesignElementClick(control: CommunicationTemplateViewControl): void {
    this.controlSelected.emit({
      template: this.template,
      control: control,
      dblClick: false
    });
    this.selectedControlUId = control.uId;
  }

  onDesignElementDblClick(control: CommunicationTemplateViewControl): void {
    this.controlSelected.emit({
      template: this.template,
      control: control,
      dblClick: true
    });
    this.selectedControlUId = control.uId;
  }

  onElementEditClick(control: CommunicationTemplateViewControl): void {
    const designControl = this.designControls?._results.find(c => c._control.uId === control.uId);
    if (designControl) {
      designControl.edit();
    }
  }

  onElementDeleteClick(control: CommunicationTemplateViewControl): void {
    this.controlDeleted.emit({
      template: this.template,
      control: control,
      dblClick: true
    });
  }

  setDocumentContext(dataset: Dataset, document: Document, mapUId: string): void {
    const mapping = this.templateDesignerService.buildDocumentFieldMapping(dataset, document, this.culture);
    this.designControls?._results?.forEach(c => {
      c.setDocumentContext(dataset, document, mapUId, mapping);
    });
  }

  setRuntimeData(controlUId: string, data: any): boolean {
    const designControl = this.designControls?._results.find(c => c._control.uId === controlUId);
    if (designControl) {
      designControl.setRuntimeData(data);
      return true;
    }
    return false;
  }

  canDragControl(control: any): boolean {
    return !(!!this.busyControls && this.busyControls.includes(control.uId));
  }

  dragenterElement(event): any {
    if (this.editable) {
      event.preventDefault();

      event.target.classList.add('dragenter');
      return false;
    }
  }

  dragleaveElement(e: any, widget: any) {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.remove('dragenter');
  }

  dragoverElement(event): any {
    if (this.editable) {
      event.preventDefault();
      return false;
    }
  }

  dropElement(dropIndex: number, event): any {
    if (!this.editable) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.target.classList.remove('dragenter');

    const actionData = event.dataTransfer.getData('text/plain');
    const action: TemplateViewControlAction = JSON.parse(actionData);

    const selectedTemplate = this.template;

    if (action.actionType === ControlActionType.Add) {
      const updatedTemplate = produce(this._template, draftState => {
        if (!!!draftState.viewControls) {
          draftState.viewControls = [];
        }
        const newControl = {
          uId: uuidv4(),
          controlType: action.controlType,
          sortOrder: draftState.viewControls?.length + 1
        };

        if (dropIndex === -1) {
          draftState.viewControls.push(newControl);
        } else {
          draftState.viewControls.splice(dropIndex, 0, newControl);
        }

        this.onDesignElementClick(newControl);

        let index = 0;
        for (const comp of draftState.viewControls) {
          comp.sortOrder = index;
          index++;
        }
      });

      this.changed.emit(updatedTemplate);
    } else if (action.actionType === ControlActionType.Move) {
      const updatedTemplate = produce(this._template, draftState => {
        const previousIndex = draftState.viewControls.findIndex(d => d.uId === action.sourceElementId);
        const currentIndex = dropIndex;
        const itemToMove = draftState.viewControls.splice(previousIndex, 1);
        if (currentIndex !== null) {
          draftState.viewControls.splice(currentIndex, 0, ...itemToMove);
        } else {
          draftState.viewControls.push(...itemToMove);
        }

        let index = 0;
        for (const comp of draftState.viewControls) {
          comp.sortOrder = index;
          index++;
        }
      });

      this.changed.emit(updatedTemplate);
    }

    return false;
  }

  dragElement(event, control: CommunicationTemplateViewControl): void {
    const properties: TemplateViewControlAction = {
      actionType: ControlActionType.Move,
      controlType: control.controlType,
      sourceElementId: control.uId
    };

    const propertyData = JSON.stringify(properties);

    event.dataTransfer.setData('text/plain', propertyData);
  }

  trackByFn(index, item: CommunicationTemplateViewControl) {
    return item.uId;
  }

  hasEdit(control: CommunicationTemplateViewControl): boolean {
    return !!this.editableControlTypes ? this.editableControlTypes.includes(control.controlType) : true;
  }

  hasDelete(control: CommunicationTemplateViewControl): boolean {
    return !!this.deletableControlTypes ? this.deletableControlTypes.includes(control.controlType) : true;
  }

  onControlChanged(template: CommunicationTemplate, control: CommunicationTemplateViewControl, event: { uId: string; settings: any }) {
    this.controlChanged.emit({ template, control, settings: event.settings });
  }

  isControlHidden(control: CommunicationTemplateViewControl): boolean {
    return !!this.hiddenControls && this.hiddenControls.includes(control.uId);
  }

  getControlDisplay(control: CommunicationTemplateViewControl): string {
    const registration = TemplateDesignElementsRegistration.find(
      r => r.channelUId === this.template.channelUId && r.viewControlType == control.controlType
    );
    if (!!registration?.desc) {
      return this.translateService.instant(registration?.desc);
    }
    return '';
  }

  onHideControl(controlId: string) {
    this.hiddenControls.push(controlId);
  }
  onShowControl(controlId: string) {
    const index = this.hiddenControls.indexOf(controlId);
    if (index >= 0) {
      this.hiddenControls.splice(index, 1);
    }
  }
  onControlBusy(controlId: string) {
    this.busyControls.push(controlId);
  }
  onControlReady(controlId: string) {
    const index = this.busyControls.indexOf(controlId);
    if (index >= 0) {
      this.busyControls.splice(index, 1);
    }
  }
}
